export type DwhChartType = "xmr" | "xmr-log";

export interface DwhColumnDef {
  key: string;
  label: string;
  unit?: string;
  chartType?: DwhChartType;
  resampleStrategy?: "sum" | "average";
  description?: string;
  owner?: string;
  tags?: string[];
}

export interface DwhStackedAreaSeries {
  columnKey: string;
  label: string;
  color?: string;
}

export interface DwhStackedAreaChartDef {
  id: string;
  label: string;
  series: DwhStackedAreaSeries[];
  unit?: string;
  owner?: string;
  tags?: string[];
}

export interface DwhQueryDef {
  id: string;
  label: string;
  /** BigQuery project to bill against. Falls back to VITE_BQ_PROJECT_ID. */
  billingProject?: string;
  /**
   * BigQuery SQL. Use {startDate} and {endDate} as ISO date placeholders (YYYY-MM-DD).
   * The date column must be named as specified by `dateColumn` (default: "day").
   */
  sql: string;
  dateColumn?: string;
  columns: DwhColumnDef[];
  stackedAreaCharts?: DwhStackedAreaChartDef[];
}

// Budget spend fullness — daily line item utilization stats
const BUDGET_SPEND_FULLNESS_SQL = `
WITH pool_currency AS (
  SELECT DISTINCT bp.id AS budget_pool_id, bp.cycle_length, c.rate
  FROM \`martin-test-datalab.postgres.budget_pools\` bp
  JOIN \`martin-test-datalab.postgres.billing_accounts\` ba ON bp.billing_account_id = ba.id
  JOIN \`martin-test-datalab.postgres.currencies\` c ON ba.currency_id = c.id
),
daily_line_items AS (
  SELECT
    DATE(oli.start_date) AS day,
    oli.budget_total / 1000000.0 / COALESCE(pc.rate, 1.0) AS budget_total_eur,
    oli.budget_used  / 1000000.0 / COALESCE(pc.rate, 1.0) AS budget_used_eur,
    SAFE_DIVIDE(oli.budget_used, oli.budget_total) AS utilization_rate,
    TIME(oli.start_date) != TIME '00:00:00' AS partial_day
  FROM \`martin-test-datalab.postgres.order_line_items\` oli
  JOIN pool_currency pc ON oli.budget_pool_id = pc.budget_pool_id
  WHERE pc.cycle_length = 2 /* 'daily' -> 2 */
    AND DATE(oli.start_date) BETWEEN '{startDate}' AND '{endDate}'
    AND DATE(oli.end_date) < CURRENT_DATE()
)
SELECT
  day,
  COUNTIF(NOT partial_day) AS full_day_line_items,
  ROUND(SAFE_DIVIDE(COUNTIF(NOT partial_day AND utilization_rate >= 1.0), COUNTIF(NOT partial_day AND utilization_rate > 0)) * 100, 1) AS pct_spent_in_full,
  ROUND(SUM(CASE WHEN NOT partial_day AND utilization_rate > 0 AND utilization_rate < 1.0  THEN budget_total_eur - budget_used_eur ELSE 0.0 END), 0) AS underspend_eur,
  ROUND(SUM(CASE WHEN NOT partial_day AND utilization_rate >= 1.0 THEN budget_used_eur - budget_total_eur ELSE 0.0 END), 0) AS overspend_eur
FROM daily_line_items
GROUP BY day
ORDER BY day
`;

// Revenue split by buying unit (CPM vs CPC)
const REVENUE_BY_BUYING_UNIT_SQL = `
SELECT
  day,
ROUND(SAFE_DIVIDE(SUM(CASE WHEN ci.buying_unit = 'impression' THEN revenue_eur ELSE 0 END), SUM(revenue_eur)) * 100, 1) AS pct_revenue_cpm,
  ROUND(SAFE_DIVIDE(SUM(CASE WHEN ci.buying_unit = 'click' THEN revenue_eur ELSE 0 END), SUM(revenue_eur)) * 100, 1) AS pct_revenue_cpc
FROM \`remerge-dwh.ops.apro_campaign_level_performance\` p
JOIN \`remerge-dwh.ops.campaign_information\` ci USING (campaign_id)
WHERE day >= DATE '{startDate}' AND day <= DATE '{endDate}'
GROUP BY day
ORDER BY day
`;

export const DWH_QUERIES: DwhQueryDef[] = [
  {
    id: "budget_spend_fullness",
    label: "Daily Line Items",
    sql: BUDGET_SPEND_FULLNESS_SQL,
    dateColumn: "day",
    columns: [
      {
        key: "pct_spent_in_full",
        label: "Spending in Full (%)",
        unit: "%",
        chartType: "xmr",
        resampleStrategy: "average",
        description:
          "Percentage of full-day daily line items that spent their entire budget. Line items with zero spend are excluded from both numerator and denominator, as these typically represent paused or inactive campaigns.",
        owner: "Sebastian",
      },
      {
        key: "underspend_eur",
        label: "Underspend (EUR)",
        unit: "EUR",
        chartType: "xmr",
        resampleStrategy: "sum",
        description:
          "Total budget not spent across full-day daily line items that had some spend activity. Line items with zero spend are excluded, as these typically represent paused or inactive campaigns rather than genuine underspend.",
        owner: "Sebastian",
        tags: ["Operations", "Advertising Performance"],
      },
      {
        key: "overspend_eur",
        label: "Overspend (EUR)",
        unit: "EUR",
        chartType: "xmr",
        resampleStrategy: "sum",
        owner: "Sebastian",
        tags: ["Operations", "Advertising Performance"],
      },
    ],
  },
  {
    id: "revenue_by_buying_unit",
    label: "Revenue by Buying Unit",
    sql: REVENUE_BY_BUYING_UNIT_SQL,
    dateColumn: "day",
    columns: [
      { key: "pct_revenue_cpm", label: "CPM Revenue (%)", unit: "%", resampleStrategy: "average" },
      { key: "pct_revenue_cpc", label: "CPC Revenue (%)", unit: "%", resampleStrategy: "average" },
    ],
    stackedAreaCharts: [
      {
        id: "revenue-cpm-cpc-split",
        label: "Revenue by Buying Unit: CPM vs CPC",
        unit: "%",
        owner: "Freddie",
        tags: ["Advertising Performance"],
        series: [
          { columnKey: "pct_revenue_cpm", label: "CPM", color: "#26B941" },
          { columnKey: "pct_revenue_cpc", label: "CPC", color: "#D2C8FF" },
        ],
      },
    ],
  },
  {
    id: "revenue_by_spend_objective",
    label: "Revenue by Spend Objective",
    sql: `
SELECT
  day,
  ROUND(SAFE_DIVIDE(SUM(CASE WHEN COALESCE(bi.spend_objective, 'Other') = 'Other' THEN revenue_eur ELSE 0 END), SUM(revenue_eur)) * 100, 1) AS pct_revenue_other,
  ROUND(SAFE_DIVIDE(SUM(CASE WHEN bi.spend_objective = 'Spend Plus' THEN revenue_eur ELSE 0 END), SUM(revenue_eur)) * 100, 1) AS pct_revenue_spend_plus,
  ROUND(SAFE_DIVIDE(SUM(CASE WHEN bi.spend_objective = 'Performance' THEN revenue_eur ELSE 0 END), SUM(revenue_eur)) * 100, 1) AS pct_revenue_performance
FROM \`remerge-dwh.ops.apro_campaign_level_performance\` p
LEFT JOIN (SELECT DISTINCT budget_pool, spend_objective FROM \`remerge-dwh.ops.budget_information\`) bi ON p.budget_id = bi.budget_pool
WHERE day >= DATE '{startDate}' AND day <= DATE '{endDate}'
GROUP BY day
ORDER BY day
`,
    dateColumn: "day",
    columns: [
      { key: "pct_revenue_other", label: "Other (%)", unit: "%", resampleStrategy: "average" },
      { key: "pct_revenue_spend_plus", label: "Spend Plus (%)", unit: "%", resampleStrategy: "average" },
      { key: "pct_revenue_performance", label: "Performance (%)", unit: "%", resampleStrategy: "average" },
    ],
    stackedAreaCharts: [
      {
        id: "revenue-spend-objective-split",
        label: "Revenue by Spend Objective",
        unit: "%",
        owner: "Sebastian",
        tags: ["Operations", "Advertising Performance"],
        series: [
          { columnKey: "pct_revenue_spend_plus", label: "Spend Plus", color: "#26B941" },
          { columnKey: "pct_revenue_other", label: "Other", color: "#BFBDA7" },
          { columnKey: "pct_revenue_performance", label: "Performance", color: "#D2C8FF" },
        ],
      },
    ],
  },
];
